import React, { useState } from "react";
import { Pencil, X } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export const ContentEditButton = ({ contentKey, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`edit-content-${contentKey}`}
    title="Edit this text (master only)"
    className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/90 border border-[#F8C8DC] text-[#E5A0BC] hover:bg-[#FCE6E9] transition-colors align-middle ml-2 shadow-sm"
  >
    <Pencil className="w-3 h-3" />
  </button>
);

export default function ContentEditModal({ contentKey, label, value, multiline = true, onClose }) {
  const [text, setText] = useState(value || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!text.trim()) {
      toast.error("Content cannot be empty.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/master/site-content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ updates: { [contentKey]: text.trim() } }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Save failed");
      window.dispatchEvent(new Event("nt-content-changed"));
      toast.success("Content updated — live across the site.");
      onClose();
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-[#374151]/30 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="content-edit-modal" onClick={onClose}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-lg w-full p-7 shadow-xl animate-modal-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4 border-b border-[#FCE6E9] pb-3">
          <h3 className="text-base font-extrabold font-heading text-[#374151]">Edit — {label}</h3>
          <button onClick={onClose} data-testid="content-edit-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        {multiline ? (
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} className="w-full px-4 py-3 rounded-2xl border border-[#FCE6E9] bg-[#FFF9F2] text-sm font-medium text-[#374151] focus:outline-none focus:border-[#F8C8DC]" data-testid="content-edit-input" />
        ) : (
          <input value={text} onChange={(e) => setText(e.target.value)} className="w-full px-4 py-3 rounded-2xl border border-[#FCE6E9] bg-[#FFF9F2] text-sm font-medium text-[#374151] focus:outline-none focus:border-[#F8C8DC]" data-testid="content-edit-input" />
        )}
        <p className="text-[10px] font-bold text-[#9CA3AF] mt-2">Key: {contentKey} — updates instantly for every visitor.</p>
        <button onClick={save} disabled={saving} data-testid="content-edit-save" className="w-full mt-4 px-5 py-3 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-sm font-extrabold transition-all disabled:opacity-50">
          {saving ? "Saving..." : "Save & Publish"}
        </button>
      </div>
    </div>
  );
}
